/**
 * @hostesa/feature-flags — Flag evaluator
 *
 * Pure evaluation logic — no fetches, no React, no side effects.
 * The evaluator consumes a typed config shape injected by the calling app
 * (typically via a loader or server-provided bootstrap payload) and merges it
 * with safe defaults.
 *
 * Precedence (highest → lowest):
 *   hotel override  >  environment override  >  config defaults  >  DEFAULT_FLAGS
 *
 * IMPORTANT: Feature flags are NOT an authorization layer.
 * A route is accessible only when session + RBAC + ABAC + flag ALL pass.
 * Never use an "enabled" flag as a substitute for a permission check.
 */

import type { FeatureFlag, FlagsRecord, PartialFlagsRecord } from './flags.js';
import { DEFAULT_FLAGS, flagsRecordSchema } from './flags.js';

// ---------------------------------------------------------------------------
// Config shape consumed by the evaluator
// ---------------------------------------------------------------------------

/**
 * The source configuration shape passed to createFlagEvaluator.
 *
 * - `defaults`: base flags for the current deployment (e.g. from env config).
 * - `environment`: overrides specific to the environment (local/staging/prod).
 * - `hotelOverrides`: per-hotel flag overrides keyed by hotel UUID.
 *
 * All fields are optional; createFlagEvaluator applies DEFAULT_FLAGS as the
 * ultimate fallback so callers only need to provide what they know.
 */
export interface FlagSource {
  /** Base flags provided by the backend bootstrap or static config. */
  defaults?: PartialFlagsRecord;
  /** Per-environment overrides (e.g. enable diagnostics in staging only). */
  environment?: PartialFlagsRecord;
  /** Per-hotel overrides keyed by hotel UUID (resolved ID, not slug). */
  hotelOverrides?: Readonly<Record<string, PartialFlagsRecord>>;
}

// ---------------------------------------------------------------------------
// Evaluator interface
// ---------------------------------------------------------------------------

export interface FlagEvaluator {
  /**
   * Resolves a single flag for the current context.
   *
   * @param flag - The flag name to evaluate.
   * @param hotelId - Optional hotel UUID. When provided, hotel-level overrides
   *   are applied on top of the environment and default layers.
   * @returns The resolved boolean value for the flag.
   */
  isEnabled(flag: FeatureFlag, hotelId?: string): boolean;

  /**
   * Returns the full resolved flags map for a given hotel context.
   *
   * @param hotelId - Optional hotel UUID for hotel-level override resolution.
   * @returns A complete FlagsRecord with all flags resolved.
   */
  resolve(hotelId?: string): FlagsRecord;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Creates a pure, immutable FlagEvaluator from the provided source config.
 *
 * The evaluator merges layers in the following precedence order:
 *   DEFAULT_FLAGS < source.defaults < source.environment < hotelOverrides[hotelId]
 *
 * Throws a ZodError if any supplied partial record fails schema validation,
 * giving early feedback on malformed backend payloads.
 *
 * @example
 * ```ts
 * const evaluator = createFlagEvaluator({
 *   defaults: { script_studio: true },
 *   environment: { livekit_handoff: false },
 *   hotelOverrides: { 'hotel-uuid-123': { in_stay_operations: true } },
 * });
 *
 * evaluator.isEnabled('in_stay_operations', 'hotel-uuid-123'); // true
 * evaluator.isEnabled('livekit_handoff');                       // false
 * ```
 */
export function createFlagEvaluator(source: FlagSource): FlagEvaluator {
  // Validate all supplied partial records up-front to catch bad payloads early.
  // parse() throws ZodError on invalid input; we intentionally let it propagate.
  const validatedDefaults = source.defaults ? flagsRecordSchema.parse(source.defaults) : {};

  const validatedEnvironment = source.environment
    ? flagsRecordSchema.parse(source.environment)
    : {};

  // Validate each hotel override record.
  const validatedHotelOverrides: Record<string, PartialFlagsRecord> = {};
  if (source.hotelOverrides) {
    for (const [hotelId, overrides] of Object.entries(source.hotelOverrides)) {
      validatedHotelOverrides[hotelId] = flagsRecordSchema.parse(overrides);
    }
  }

  function resolve(hotelId?: string): FlagsRecord {
    const hotelLayer = hotelId !== undefined ? (validatedHotelOverrides[hotelId] ?? {}) : {};

    return {
      ...DEFAULT_FLAGS,
      ...validatedDefaults,
      ...validatedEnvironment,
      ...hotelLayer,
    };
  }

  function isEnabled(flag: FeatureFlag, hotelId?: string): boolean {
    return resolve(hotelId)[flag];
  }

  return { isEnabled, resolve };
}

// ---------------------------------------------------------------------------
// Route-guard helper (non-React)
// ---------------------------------------------------------------------------

/**
 * Pure route-guard helper for use in TanStack Router beforeLoad functions
 * or any non-React guard context.
 *
 * Returns `{ ok: true }` when the flag is enabled, `{ blocked: true }` when
 * the flag is disabled.
 *
 * Usage:
 * ```ts
 * const result = flagGate('in_stay_operations', evaluator, hotelId);
 * if ('blocked' in result) { throw redirect({ to: '/reception' }); }
 * ```
 *
 * IMPORTANT: Passing this guard does not grant access.
 * The calling route guard must ALSO verify session, RBAC, and ABAC.
 * Feature flags are a UX layer on top of the full auth stack.
 */
export function flagGate(
  flag: FeatureFlag,
  evaluator: FlagEvaluator,
  hotelId?: string,
): { ok: true } | { blocked: true } {
  return evaluator.isEnabled(flag, hotelId) ? { ok: true } : { blocked: true };
}
